/** Display labels for canonical ontology slugs — user-facing only, no internals exposed. */

export const FORMAT_LABELS = {
  vr: 'Virtual Reality',
  ar: 'Augmented Reality',
  webxr: 'WebXR',
  lbe: 'LBE',
  '360-video': '360°',
  vr180: 'VR180',
  'spatial-video': 'Spatial Video',
  'immersive-theater': 'Immersive Theater',
  installation: 'Installation',
  'escape-room': 'Escape Room',
  fulldome: 'Fulldome',
  'projection-mapping': 'Projection Mapping',
  'social-vr': 'Social VR',
  '360-live-stream': '360° Live Stream',
  'audio-walk': 'Audio Walk'
};

export const VENUE_MODEL_LABELS = {
  anywhere: 'Anywhere',
  'at-home': 'At Home',
  'specific-venue': 'Specific Venue',
  'gps-anchored': 'GPS Anchored',
  touring: 'Touring',
  'virtual-world': 'Virtual World',
  hybrid: 'Hybrid'
};

export const PRICE_TYPE_LABELS = {
  free: 'Free',
  paid: 'Paid',
  ticket: 'Ticketed',
  pwyw: 'Pay What You Wish'
};

export const COMFORT_LABELS = {
  comfortable: 'Comfortable',
  moderate: 'Moderate',
  intense: 'Intense',
  extreme: 'Extreme'
};

export const AGE_LABELS = {
  'all-ages': 'All Ages',
  '7+': 'Ages 7+',
  '13+': 'Ages 13+',
  '18+': 'Ages 18+'
};

export const SESSION_STRUCTURE_LABELS = {
  'single-session': 'Single Session',
  'drop-in': 'Drop-In',
  persistent: 'Persistent World',
  'live-event': 'Live Event',
  episodic: 'Episodic'
};

export const PLATFORM_LABELS = {
  browser: 'Browser',
  quest: 'Meta Quest',
  'vision-pro': 'Apple Vision Pro',
  pcvr: 'PC VR',
  steamvr: 'SteamVR',
  psvr: 'PlayStation VR',
  mobile: 'Mobile',
  ios: 'iOS',
  android: 'Android',
  lbe: 'LBE'
};

export const DEVICE_LABELS = {
  browser: 'Browser',
  quest: 'Meta Quest',
  'vision-pro': 'Apple Vision Pro',
  pcvr: 'PC VR',
  steamvr: 'SteamVR',
  mobile: 'Mobile',
  ios: 'iPhone / iPad',
  android: 'Android',
  phone: 'iPhone / iPad',
  'phone-ar': 'Phone AR',
  lbe: 'LBE'
};

export const ACTIVITY_LABELS = {
  play: 'Play',
  explore: 'Explore',
  watch: 'Watch',
  learn: 'Learn',
  socialize: 'Socialize',
  create: 'Create',
  perform: 'Perform'
};

export const THEME_LABELS = {
  'sci-fi': 'Sci-Fi',
  horror: 'Horror',
  art: 'Art',
  music: 'Music',
  nature: 'Nature',
  comedy: 'Comedy',
  education: 'Education',
  narrative: 'Narrative'
};

export const LOCOMOTION_LABELS = {
  stationary: 'Stationary',
  seated: 'Seated',
  'room-scale': 'Room Scale',
  walking: 'Walking Required'
};

export const CONTENT_NOTE_LABELS = {
  flashing: 'Flashing Lights',
  heights: 'Heights',
  loud: 'Loud Audio',
  mature: 'Mature Themes'
};

export const ACCESSIBILITY_LABELS = {
  'wheelchair-accessible': 'Wheelchair Accessible',
  'audio-description': 'Audio Description',
  captions: 'Captions',
  'seated-option': 'Seated Option',
  'no-jumpscares': 'No Jump Scares'
};

export const VERIFIED_BY_LABELS = {
  creator: 'Creator',
  realitycheck: 'RealityCheck',
  community: 'Community'
};

export const PHYSICAL_VENUE_MODELS = ['specific-venue', 'touring', 'gps-anchored'];

export const LINEAR_FORMATS = ['360-video', 'vr180', 'spatial-video', '360-live-stream'];

export const SOCIAL_FORMATS = ['social-vr', 'metaverse'];

/** Headset, browser, and streamed formats where VR comfort / locomotion apply. */
export const DIGITAL_XR_FORMATS = new Set([
  'vr',
  'ar',
  'webxr',
  'social-vr',
  '360-video',
  'vr180',
  'spatial-video',
  '360-live-stream'
]);

export const REPLAYABILITY_LABELS = {
  high: 'High: different each visit',
  medium: 'Moderate replay value',
  low: 'One-time experience'
};

export const GUIDANCE_LEVEL_LABELS = {
  'self-guided': 'Self-guided',
  guided: 'Guided',
  'staff-assisted': 'Staff-assisted',
  hybrid: 'Self-guided with staff'
};

/** Physical venue visits — locomotion and VR comfort ratings do not apply. */
export function isPhysicalPrimaryFormat(primaryFormat) {
  if (!primaryFormat) return false;
  return !DIGITAL_XR_FORMATS.has(primaryFormat);
}

export function isComfortRatingMeaningful(primaryFormat) {
  return !!primaryFormat && DIGITAL_XR_FORMATS.has(primaryFormat);
}

export function labelFromMap(map, slug) {
  if (!slug) return null;
  const key = String(slug).toLowerCase();
  return map[key] || formatSlugAsLabel(slug);
}

export function formatSlugAsLabel(slug) {
  if (!slug) return '';
  const key = String(slug).toLowerCase();
  if (key === 'lbe') return 'LBE';
  return String(slug)
    .split('-')
    .map((w) => (w.toLowerCase() === 'lbe' ? 'LBE' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

export function labelsFromSlugs(slugs, map) {
  if (!Array.isArray(slugs)) return [];
  return slugs.map((s) => labelFromMap(map, s)).filter(Boolean);
}

/** Normalize legacy comfort strings to slug form (comfortable, moderate, intense, extreme). */
export function normalizeComfortSlug(value) {
  if (!value) return null;
  let v = String(value).toLowerCase().trim();
  // "Moderate Comfort", "Comfortable Comfort" → strip redundant suffix before matching
  v = v.replace(/\s+comfort\s*$/, '').trim();
  if (!v || v === 'comfort') return null;
  if (v.includes('comfortable')) return 'comfortable';
  if (v.includes('moderate')) return 'moderate';
  if (v.includes('intense')) return 'intense';
  if (v.includes('extreme')) return 'extreme';
  if (COMFORT_LABELS[v]) return v;
  return null;
}

export function formatComfortLevel(value) {
  const slug = normalizeComfortSlug(value);
  if (slug && COMFORT_LABELS[slug]) return COMFORT_LABELS[slug];

  if (value && typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // Last resort: strip a trailing " Comfort" from legacy display strings
    const withoutSuffix = trimmed.replace(/\s+comfort\s*$/i, '').trim();
    if (withoutSuffix !== trimmed) {
      const legacySlug = normalizeComfortSlug(withoutSuffix);
      if (legacySlug && COMFORT_LABELS[legacySlug]) return COMFORT_LABELS[legacySlug];
    }
    return trimmed;
  }
  return null;
}

/** Quick Facts line — e.g. "Comfortable", "Moderate", "Intense" */
export function formatComfortQuickFact(value) {
  return formatComfortLevel(value);
}

/** Accessibility & Comfort line — e.g. "Comfortable · No locomotion concerns" */
export function formatComfortAccessibilityLine(comfortRating, locomotionTypes) {
  const level = formatComfortLevel(comfortRating);
  if (!level) return null;
  const hasLocomotion = Array.isArray(locomotionTypes) && locomotionTypes.length > 0;
  if (!hasLocomotion) return `${level} · No locomotion concerns`;
  return level;
}

/** User-facing comfort label used in quick facts and reference UI. */
export function formatComfortDisplay(value) {
  return formatComfortLevel(value);
}

/** Normalize legacy age strings like "12+" or "all" to slug form. */
export function normalizeAgeSlug(value) {
  if (!value) return null;
  const v = String(value).toLowerCase().trim();
  if (v === 'all' || v.includes('all ages')) return 'all-ages';
  if (v.includes('18')) return '18+';
  if (v.includes('13')) return '13+';
  if (v.includes('7')) return '7+';
  if (/^\d+\+$/.test(v)) return v.replace('all', 'all-ages');
  return v.includes('+') ? v : null;
}

export function formatAgeDisplay(value) {
  const slug = normalizeAgeSlug(value) || (AGE_LABELS[value] ? value : null);
  if (slug && AGE_LABELS[slug]) return AGE_LABELS[slug];
  if (value && typeof value === 'string') {
    const normalized = normalizeAgeSlug(value);
    if (normalized) return AGE_LABELS[normalized];
    if (value.includes('+')) return `Ages ${value}`;
    return value;
  }
  return null;
}

/** Pick a contextual emoji for Come Prepared tips (ASCII-safe matching). */
export function getComePreparedEmoji(tip) {
  const t = String(tip).toLowerCase();
  if (/ticket|book/.test(t)) return '\u{1F3AB}';
  if (/shoe|wear|comfortable/.test(t)) return '\u{1F45F}';
  if (/arrive|timed|entry/.test(t)) return '\u{23F0}';
  if (/headset|quest|charge|battery/.test(t)) return '\u{1F97D}';
  if (/space|clear|area|room|play area/.test(t)) return '\u{1F4CF}';
  if (/wifi|internet|connection/.test(t)) return '\u{1F4F6}';
  if (/headphone|audio|sound|music/.test(t)) return '\u{1F3A7}';
  if (/camera|permission|safari|chrome|browser|phone/.test(t)) return '\u{1F4F1}';
  if (/fullscreen|drag|look around|move your device/.test(t)) return '\u{1F440}';
  if (/light|lit|lighting/.test(t)) return '\u{1F4A1}';
  if (/strap|wrist/.test(t)) return '\u{1F517}';
  if (/surface|flat/.test(t)) return '\u{1F4CD}';
  return '\u{1F4CB}';
}
